/**
 * Application Release Notes & Version History
 * 
 * Instructions for Agents:
 * Every version release (PATCH, MINOR, or MAJOR) MUST add a new entry to the top of RELEASE_NOTES.
 * Include:
 *  - version: Semantic version string matching package.json (e.g., '2.11.0')
 *  - date: Release date string in 'YYYY-MM-DD' format
 *  - title: Brief, high-level summary of the release
 *  - changes: Array of concise, user-facing change items ({ type: 'feat'|'fix'|'style'|'refactor', text: '...' })
 */

export const RELEASE_NOTES = [
  {
    version: '2.18.6',
    date: '2026-10-30',
    title: 'Discord Copy Modal with Nitro Support',
    changes: [
      { type: 'feat', text: 'Added a dedicated Discord Copy modal that opens when tapping Copy for Discord' },
      { type: 'feat', text: 'Each pasteable Discord message is shown in its own card with a per-message Copy button and character count' },
      { type: 'feat', text: 'Added a Free (2,000) / Nitro (4,000) character limit toggle so Nitro users can fit more records per message' },
      { type: 'fix', text: 'Discord export chunk sizing is now tighter so fewer stacks are unnecessarily split' }
    ]
  },
  {
    version: '2.18.5',
    date: '2026-10-30',
    title: 'Filter Toolbar Polish',
    changes: [
      { type: 'fix', text: 'Removed duplicated dropdown carets by relying on the native select arrow instead of an overlapping custom chevron' },
      { type: 'style', text: 'Category and status filter sliders are now aligned to opposite sides of the panel on desktop for clearer differentiation' }
    ]
  },
  {
    version: '2.18.4',
    date: '2026-10-30',
    title: 'Filter Toolbar Layout Rebalance',
    changes: [
      { type: 'style', text: 'Category and status filter tabs are now grouped together on one centered row, with the health goal, grouping, and sort dropdowns sharing a single centered row beneath them' },
      { type: 'style', text: 'All three dropdowns now share consistent sizing, matching icons, and custom chevron indicators so the filter area feels cohesive on desktop' }
    ]
  },
  {
    version: '2.18.3',
    date: '2026-10-30',
    title: 'Discord Chat Compact Format & No Record Omission',
    changes: [
      { type: 'feat', text: 'Redesigned Discord Chat export as a compact pipe-separated format (Name, Status, Dosage, Schedule, Type, Goal) to fit far more records in a single Discord message' },
      { type: 'fix', text: 'Discord Chat export no longer omits records when the stack is long; instead it splits the output into numbered chunks for separate Discord messages' },
      { type: 'style', text: 'Column selector is hidden while Discord Chat format is active because it uses a fixed compact column set' }
    ]
  },
  {
    version: '2.18.2',
    date: '2026-10-30',
    title: 'Mobile Export Toolbar Layout Fix',
    changes: [
      { type: 'fix', text: 'Prevented export/action button labels from wrapping on mobile so the toolbar rows stay the same height' },
      { type: 'style', text: 'Replaced space-x with gap utilities in the toolbar for cleaner flex-wrap spacing' }
    ]
  },
  {
    version: '2.18.1',
    date: '2026-10-30',
    title: 'Discord Chat Format Redesign',
    changes: [
      { type: 'fix', text: 'Redesigned Discord Chat export as compact bold-heading + bullet fields so it stays readable when Discord wraps long lines' },
      { type: 'fix', text: 'Removed the fixed-width code-block table format because Discord code blocks wrap wide tables and break column alignment' },
      { type: 'style', text: 'Sanitizes exported values to prevent unintended Discord markdown formatting' }
    ]
  },
  {
    version: '2.18.0',
    date: '2026-10-30',
    title: 'Discord Chat Export Format',
    changes: [
      { type: 'feat', text: 'Added a Discord Chat export option to the Export Table modal' },
      { type: 'feat', text: 'Discord Chat format copies a fixed-width ASCII table wrapped in a code block for easy pasting into Discord messages' },
      { type: 'feat', text: 'Discord Chat output automatically trims records to stay within Discord\'s 2000-character message limit' },
      { type: 'style', text: 'Added format hints explaining that Markdown tables render when uploaded to Discord as a .md file' }
    ]
  },
  {
    version: '2.17.6',
    date: '2026-10-20',
    title: 'Serum Chart Date Label Overlap Fix',
    changes: [
      { type: 'fix', text: 'Fixed overlapping date labels at the right end of the serum chart x-axis' },
      { type: 'style', text: 'The final date label now swaps in for the nearest tick when there is not enough room, so the first and last dates always stay readable' }
    ]
  },
  {
    version: '2.17.5',
    date: '2026-10-20',
    title: 'Serum Chart Preserves Ended Cycle History',
    changes: [
      { type: 'fix', text: 'Starting a new protocol no longer overwrites the previous cycle on the serum chart' },
      { type: 'fix', text: 'Backwards extrapolation of a recently started protocol is now capped at the day after the latest completed cycle for that compound' },
      { type: 'fix', text: 'Completed cycle doses remain visible in the past, while the new dose curve begins on its real start date' }
    ]
  },
  {
    version: '2.17.4',
    date: '2026-10-20',
    title: 'Serum Projection Stops Ended Protocols',
    changes: [
      { type: 'fix', text: 'Serum projections now stop ended protocols at their end date instead of extrapolating the old dose into the future' },
      { type: 'fix', text: 'Steady-state peaks and status now reflect the most recently started active protocol, so a new dose level replaces the previous one' },
      { type: 'fix', text: 'Paused protocols no longer project future doses, while their historical doses still appear on the chart' }
    ]
  },
  {
    version: '2.17.3',
    date: '2026-10-20',
    title: 'Mobile PWA Auth Sync Hang Fix',
    changes: [
      { type: 'fix', text: 'Added a 10-second safety timeout around Firestore protocol subscriptions so the loading spinner can never hang indefinitely' },
      { type: 'fix', text: 'Forced a fresh subscription handshake when the PWA is foregrounded after being in the background, recovering from stale Firebase auth tokens' },
      { type: 'feat', text: 'Added a Retry action to the sync error banner so users can manually reconnect without restarting the app' }
    ]
  },
  {
    version: '2.17.2',
    date: '2026-10-20',
    title: 'Mobile Add Protocol CTA & Modal Zoom Usability Improvements',
    changes: [
      { type: 'feat', text: 'Promoted Add Protocol to a dedicated prominent primary CTA row below navigation tabs on mobile, always visible without horizontal scrolling' },
      { type: 'fix', text: 'Enforced 16px font-size minimum on mobile inputs to eliminate automatic iOS Safari viewport zoom-in when tapping form fields' },
      { type: 'fix', text: 'Removed touch gesture-blocking overscroll restrictions and enabled backdrop vertical scroll to ensure modals remain freely scrollable and pinch-zoomable' }
    ]
  },
  {
    version: '2.17.1',
    date: '2026-10-15',
    title: 'Mobile Header Sign-in and Sign-out Overflow Fix',
    changes: [
      { type: 'fix', text: 'Fixed mobile header button alignment to prevent Sign-in and Sign-out buttons from being pushed off-screen on compact devices' },
      { type: 'fix', text: 'Optimized mobile brand header spacing and title truncation ensuring release badge and auth controls remain fully in view' },
      { type: 'style', text: 'Added smooth horizontal scrolling with hidden scrollbar for mobile navigation and header action controls' }
    ]
  },
  {
    version: '2.17.0',
    date: '2026-10-15',
    title: 'Group Stack by Time of Day & Alphabetical Group Selector',
    changes: [
      { type: 'feat', text: 'Added Group by Time of Day option to organize active stack cards into Morning / AM, Midday / Noon, Evening / Bedtime, Multi-Slot, and PRN sections' },
      { type: 'feat', text: 'Alphabetically sorted group options in stack toolbar while keeping Group: Off as the default first option' },
      { type: 'feat', text: 'Color-coded group section headers with collapsible toggles and distinct indicator dots for every dosing time slot' }
    ]
  },
  {
    version: '2.16.0',
    date: '2026-10-15',
    title: 'Protocol Timing Info Bubble & Calendar Slot Mapping',
    changes: [
      { type: 'feat', text: 'Interactive Timing info bubble overlay patterned after Frequency Schedule with quick-insert examples categorized by calendar slot' },
      { type: 'feat', text: 'Real-time calendar slot indicator badge below Timing input previewing Morning (AM), Midday (Noon), Evening (Bedtime), Multi-Slot, or PRN placement' },
      { type: 'feat', text: 'Expanded calendar regimen timing slot parser to automatically recognize workout, breakfast, and sleep keywords' }
    ]
  },
  {
    version: '2.15.0',
    date: '2026-10-13',
    title: 'Stack Grouping Preference Persistence',
    changes: [
      { type: 'feat', text: 'Remember protocol stack card grouping selection (Off, Health Goal, or Compound Type) across page refreshes and sessions' },
      { type: 'feat', text: 'Persist grouping preferences per user in localStorage with seamless fallback for guest and demo sessions' }
    ]
  },
  {
    version: '2.14.0',
    date: '2026-10-13',
    title: 'Concurrency Hardening, Security Headers & Code Quality Refactor',
    changes: [
      { type: 'fix', text: 'Eliminated auto-status transition feedback loop and added pending-operation guards across all write handlers to prevent duplicate protocols, statuses, and audit logs' },
      { type: 'fix', text: 'Made Firestore and localStorage subscriptions the single source of truth; removed manual state patches that could transiently drop or duplicate stack data' },
      { type: 'feat', text: 'Upgraded Firebase SDK and production server dependencies; reduced high-severity npm audit findings' },
      { type: 'feat', text: 'Added Firebase Hosting security headers including Content-Security-Policy-Report-Only, X-Content-Type-Options, Referrer-Policy, and Permissions-Policy' },
      { type: 'fix', text: 'Hardened CSV export against formula injection and restricted service worker caching to same-origin static assets only' },
      { type: 'feat', text: 'Tightened Firestore security rules with field allow-lists, string size caps, and checkpoint size limits' },
      { type: 'refactor', text: 'Unified duplicated filter, dosage formatting, category normalization, localStorage, and subscription helpers across the codebase' }
    ]
  },
  {
    version: '2.13.1',
    date: '2026-10-12',
    title: 'Compound Type Dot Colors & Collapsible Grouped Sections',
    changes: [
      { type: 'style', text: 'Color-coded group indicator dots when grouped by compound type (purple for Peptides, red/rose for Hormones & AAS, blue/sky for Supplements)' },
      { type: 'feat', text: 'Made grouped stack sections collapsible with interactive header toggles, rotate chevrons, and global Expand All / Collapse All controls' }
    ]
  },
  {
    version: '2.13.0',
    date: '2026-10-12',
    title: 'Protocol Health Target Categories, Grouping & Sorting',
    changes: [
      { type: 'feat', text: 'Added Primary and Secondary Health Target categories (Cardiovascular, Cognitive/Energy, Metabolic, Sleep, Hormones, etc.) to protocol tracking' },
      { type: 'feat', text: 'Interactive Dashboard Group By toggle allowing stack protocols to be visually sectioned by Health Goal or Compound Type with count badges' },
      { type: 'feat', text: 'New Health Category (A–Z) sorting option and quick Health Goal filter dropdown' },
      { type: 'style', text: 'Rendered sleek color-coded category pills and subtle secondary target badges on protocol cards and exported images' },
      { type: 'feat', text: 'Integrated Primary and Secondary health goals into CSV and text/markdown table stack exports' }
    ]
  },
  {
    version: '2.12.0',
    date: '2026-10-08',
    title: 'Configurable Week Start & Regimen View Defaults',
    changes: [
      { type: 'feat', text: 'Set Sunday–Saturday as the new default calendar layout across Month and Week views' },
      { type: 'feat', text: 'Added Start Sun / Start Mon week toggle to switch and save preferred week start day' },
      { type: 'feat', text: 'Persist active calendar view (Day, Week, or Month) per authenticated user to restore on future visits' }
    ]
  },
  {
    version: '2.11.2',
    date: '2026-10-08',
    title: 'Calendar Category Color Harmony',
    changes: [
      { type: 'style', text: 'Aligned calendar category highlighting (rose for Hormones & AAS, purple for Peptides, sky for Supplements) to match Stack dashboard styling' }
    ]
  },
  {
    version: '2.11.1',
    date: '2026-10-07',
    title: 'Frequency Shorthand & Schedule Format Guide',
    changes: [
      { type: 'fix', text: 'Intelligently interpret shorthand like "every 3", "every 3rd", or standalone numbers as recurring day intervals' },
      { type: 'feat', text: 'Interactive frequency schedule info bubble overlay with quick-insert examples for day intervals, days of week, shorthand, and cycles' }
    ]
  },
  {
    version: '2.11.0',
    date: '2026-10-07',
    title: 'Release Notes Modal & Version History',
    changes: [
      { type: 'feat', text: 'Interactive release notes modal accessible by clicking any version badge or footer version number' },
      { type: 'feat', text: 'Monthly grouped and expandable release history with quick expand/collapse toggles' },
      { type: 'feat', text: 'Concise public-facing highlights for features, enhancements, and bug fixes with live search' }
    ]
  },
  {
    version: '2.10.0',
    date: '2026-10-07',
    title: 'Natural Language Frequency Parsing',
    changes: [
      { type: 'feat', text: 'Intelligent natural language parser for protocol frequency schedules in the calendar regimen' },
      { type: 'feat', text: 'Supports intuitive dosing patterns like "every other day", "twice weekly", and custom recurring intervals' }
    ]
  },
  {
    version: '2.9.4',
    date: '2026-10-07',
    title: 'Serum Tooltip & Live Inspector',
    changes: [
      { type: 'fix', text: 'Fixed right-edge tooltip clipping on the pharmacokinetic serum concentration chart' },
      { type: 'fix', text: 'Connected Inspect Live Data toggle directly with the live Firestore debug inspector panel' }
    ]
  },
  {
    version: '2.9.3',
    date: '2026-10-07',
    title: 'Dosage Unit Synchronization',
    changes: [
      { type: 'fix', text: 'Synchronized measurement units in audit logs across dosage transitions when updating protocols' }
    ]
  },
  {
    version: '2.9.2',
    date: '2026-10-07',
    title: 'Live Protocol Diagnostics',
    changes: [
      { type: 'feat', text: 'Added live protocol inspector and window diagnostics to inspect real-time Firestore synchronization' }
    ]
  },
  {
    version: '2.9.1',
    date: '2026-10-07',
    title: 'Active Protocol Cycle Overlap',
    changes: [
      { type: 'fix', text: 'Active protocols cleanly override overlapping historical cycles and respect user-stated start dates' }
    ]
  },
  {
    version: '2.9.0',
    date: '2026-10-07',
    title: 'URL Slug Routing & Deep Linking',
    changes: [
      { type: 'feat', text: 'Added browser URL slug routing (/stack, /tools, /serum, /calendar) with full browser back/forward history navigation support' }
    ]
  },
  {
    version: '2.8.5',
    date: '2026-10-07',
    title: 'Mobile Month Grid & Week View Affordance',
    changes: [
      { type: 'feat', text: 'Enhanced mobile calendar month grid with day agenda listing' },
      { type: 'feat', text: 'Added week view auto-center and intuitive swipe affordance on touch screens' }
    ]
  },
  {
    version: '2.8.4',
    date: '2026-10-07',
    title: 'Serum Spline Interpolation & 6-Month Horizon',
    changes: [
      { type: 'fix', text: 'Eliminated serum baseline dips using monotone cubic spline interpolation' },
      { type: 'feat', text: 'Expanded pharmacokinetic curve time horizon to 6 months and full protocol history' }
    ]
  },
  {
    version: '2.8.1',
    date: '2026-10-07',
    title: 'Pharmacokinetic Serum Extrapolation',
    changes: [
      { type: 'feat', text: 'Interactive serum concentration pharmacokinetics extrapolation chart' },
      { type: 'feat', text: 'Automated historical dose reconstruction and elimination curves' }
    ]
  },
  {
    version: '2.7.1',
    date: '2026-10-07',
    title: 'Regimen Calendar Launch',
    changes: [
      { type: 'feat', text: 'Introduced Regimen Calendar with Day, Week, and Month layouts' },
      { type: 'feat', text: 'Flexible time slot scheduling (Morning, Afternoon, Evening, Bedtime) for compound administrations' }
    ]
  },
  {
    version: '2.6.2',
    date: '2026-10-07',
    title: 'Date Picker Visual Contrast',
    changes: [
      { type: 'style', text: 'Enhanced calendar icon brightness and contrast in dark input fields' }
    ]
  },
  {
    version: '2.6.0',
    date: '2026-10-04',
    title: 'Hormones & AAS Category & Weekly Dosing',
    changes: [
      { type: 'feat', text: 'Added dedicated Hormones & AAS category with specialized tracking fields' },
      { type: 'feat', text: 'Added weekly total dosage calculation field for cyclical regimens' }
    ]
  },
  {
    version: '2.5.0',
    date: '2026-10-01',
    title: 'Native Firebase Password Reset',
    changes: [
      { type: 'feat', text: 'Integrated self-service Firebase password reset flow in authentication modal' }
    ]
  },
  {
    version: '2.4.0',
    date: '2026-10-01',
    title: 'Landing Hero & Authentication Gating',
    changes: [
      { type: 'feat', text: 'Modern unauthenticated landing page highlighting protocol tracking capabilities' },
      { type: 'feat', text: 'Seamless demo mode exploring without creating an account' }
    ]
  },
  {
    version: '2.3.0',
    date: '2026-10-01',
    title: 'Multi-User Cloud Sync & Onboarding',
    changes: [
      { type: 'feat', text: 'Full Cloud Firestore multi-user synchronization with real-time listeners' },
      { type: 'feat', text: 'Interactive onboarding wizard with sample stack templates' }
    ]
  },
  {
    version: '2.1.0',
    date: '2026-10-01',
    title: 'Stack Recovery Checkpoints',
    changes: [
      { type: 'feat', text: 'Create and restore point-in-time stack recovery checkpoints' },
      { type: 'feat', text: 'Safe protocol stack clearing with automatic pre-clear backup' }
    ]
  },
  {
    version: '2.0.0',
    date: '2026-10-01',
    title: 'Tools Hub & Reconstitution Calculator',
    changes: [
      { type: 'feat', text: 'Launched Tools Hub ecosystem' },
      { type: 'feat', text: 'Peptide Reconstitution Calculator with tick marks, vial mass, and injection volume guides' }
    ]
  },
  {
    version: '1.13.0',
    date: '2026-08-21',
    title: 'Pending Status & Automated Lifecycle',
    changes: [
      { type: 'feat', text: 'Added Pending status with automated date-based lifecycle transitions' }
    ]
  },
  {
    version: '1.12.0',
    date: '2026-08-21',
    title: 'Protocol Duplication',
    changes: [
      { type: 'feat', text: 'Quick duplicate action to clone protocol parameters and adjustments' }
    ]
  },
  {
    version: '1.10.0',
    date: '2026-08-04',
    title: 'Export Table Modal & Customizable Columns',
    changes: [
      { type: 'feat', text: 'Export stack to Markdown and Plain Text with column selection' }
    ]
  },
  {
    version: '1.8.0',
    date: '2026-07-25',
    title: 'Export as Image & Metric Cards',
    changes: [
      { type: 'feat', text: 'Export protocol stack as shareable, styled image cards' },
      { type: 'feat', text: 'Interactive protocol summary metrics with active compound counts' }
    ]
  },
  {
    version: '1.5.0',
    date: '2026-07-21',
    title: 'CSV Protocol Stack Export',
    changes: [
      { type: 'feat', text: 'Full CSV protocol stack export for spreadsheet analysis' }
    ]
  }
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Groups release notes by month and year.
 * Returns an array of objects:
 * [
 *   {
 *     monthKey: '2026-10',
 *     monthLabel: 'October 2026',
 *     releases: [...]
 *   },
 *   ...
 * ]
 */
export function groupReleaseNotesByMonth(notes = RELEASE_NOTES) {
  const groupsMap = new Map();

  for (const item of notes) {
    if (!item.date) continue;
    const [yearStr, monthStr] = item.date.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const monthKey = `${year}-${String(month).padStart(2, '0')}`;
    const monthLabel = !isNaN(year) && !isNaN(month) && month >= 1 && month <= 12
      ? `${MONTH_NAMES[month - 1]} ${year}`
      : 'Previous Releases';

    if (!groupsMap.has(monthKey)) {
      groupsMap.set(monthKey, {
        monthKey,
        monthLabel,
        year,
        month,
        releases: []
      });
    }

    groupsMap.get(monthKey).releases.push(item);
  }

  // Sort descending by monthKey (e.g. 2026-10 before 2026-08)
  return Array.from(groupsMap.values()).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
}

/**
 * Formats a YYYY-MM-DD date into a readable string like 'Oct 7, 2026'
 */
export function formatReleaseDate(dateStr) {
  if (!dateStr) return '';
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return dateStr;
    const dateObj = new Date(year, month - 1, day);
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
}
